import { useEffect, useRef } from 'react';
import Parser from 'html-react-parser';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import Card from '../../../../../common/card';
import CardDescription from '../../../../../common/card-description';
import CardTitle from '../../../../../common/card-title';
import l, { langKeyExists } from '../../../../../../lang';
import UserIcon from '../../../../../../assets/icons/user.svg';
import BuildingIcon from '../../../../../../assets/icons/building.svg';
import '../styles.scss';
import config from '../../../../../../config';
import UploadFile from '../../../../../common/upload-file';
import ButtonCustom from '../../../../../common/__deprecated__/button-custom';
import { Formik, FormikErrors, FormikValues } from 'formik';
import PopupInElement from '../../../../../common/popup-in-element';
import { useMediaQuery } from '@mui/material';
import { FormObserver } from './form-observer';
import { ItemOnboardingMenu } from '../../../../onboarding-menu';
import FileIcon from '../../../../../../assets/icons/file.svg';
import MessageError from '../../../../../common/message-error';
import { scrollIntoView } from '../../../../../../utils';
import {
  segmentKycFormL2UploadDocAddress,
  segmentKycFormL2UploadDocBank,
  segmentKycFormL2UploadDocCompanyIncorporation,
  segmentKycFormL2UploadDocCompanyTax,
  segmentKycFormL2UploadDocOwner,
} from '../../../../../../utils/analytics';

export type ValuesLevelTwoStepDocs = {
  bankAccountDocument: string | undefined;
  identityDocument: string | undefined;
  proofOfAddressDocument: string | undefined;
  taxCompliantDocument: string | undefined;
  shareholdersDocument: string | undefined;
};

interface SectionOneProps {
  error: boolean;
  errorInfo: object;
  displayed: boolean;
  isCompany: boolean;
  missingDocuments: string[];
  country: string;
  onClickNext: (values: ValuesLevelTwoStepDocs) => void;
  onChangeMenu: (menu: ItemOnboardingMenu[]) => void;
}

const SectionDocs = ({
  error,
  errorInfo,
  displayed,
  isCompany,
  missingDocuments,
  onClickNext,
  onChangeMenu,
  country,
}: SectionOneProps): JSX.Element => {
  const isTabletMobile = useMediaQuery('(max-width:768px)');
  const errorRef = useRef(null);

  useEffect(() => {
    if (error) {
      scrollIntoView(errorRef);
    }
  }, [error]);

  const docsRequired = {
    bankAccountDocument: !missingDocuments || missingDocuments?.includes('bankAccountDocument'),
    identityDocument: !missingDocuments || missingDocuments?.includes('identityDocument'),
    proofOfAddressDocument: !missingDocuments || missingDocuments?.includes('proofOfAddressDocument'),
    taxCompliantDocument: !missingDocuments || missingDocuments?.includes('taxCompliantDocument'),
    shareholdersDocument: !missingDocuments || missingDocuments?.includes('shareholdersDocument'),
  };

  const initialValues: ValuesLevelTwoStepDocs = {
    bankAccountDocument: undefined,
    identityDocument: undefined,
    proofOfAddressDocument: undefined,
    taxCompliantDocument: undefined,
    shareholdersDocument: undefined,
  };

  const validate = (values: FormikValues) => {
    const errors: FormikErrors<FormikValues> = {};
    if (docsRequired.bankAccountDocument) {
      if (!values.bankAccountDocument) {
        errors.bankAccountDocument = l('form.input.required');
      }
    }

    if (docsRequired.identityDocument) {
      if (!values.identityDocument) {
        errors.identityDocument = l('form.input.required');
      }
    }

    if (docsRequired.proofOfAddressDocument) {
      if (!values.proofOfAddressDocument) {
        errors.proofOfAddressDocument = l('form.input.required');
      }
    }

    if (docsRequired.taxCompliantDocument) {
      if (!values.taxCompliantDocument) {
        errors.taxCompliantDocument = l('form.input.required');
      }
    }
    if (docsRequired.shareholdersDocument) {
      if (isCompany && !values.shareholdersDocument) {
        errors.shareholdersDocument = l('form.input.required');
      }
    }

    return errors;
  };

  return (
    <div>
      <div className="kyc-level-2_section">
        <Card>
          <CardTitle>{l('companyInformation.kycTwo.sectionOne.title')}</CardTitle>
          <CardDescription>{l('companyInformation.kycTwo.sectionOne.subtitle')}</CardDescription>
        </Card>
      </div>
      <div className="kyc-level-2_section">
        <p className="kyc-level-2_tooptip_fixed">
          <LightbulbOutlinedIcon color="primary" fontSize={'large'}></LightbulbOutlinedIcon>
          {Parser(
            l('companyInformation.kycTwo.tooltip', config.uloadFileMaxSize, config.uloadFileSupportedExtensions.join(', '))
          )}
        </p>

        <Formik initialValues={initialValues} validate={validate} onSubmit={values => onClickNext(values)}>
          {({ errors, touched, handleSubmit, setFieldValue }) => {
            const onFileBankHandler = ({ file: value }: { file: File | null; base64: string | null }): void => {
              if (value !== null) {
                segmentKycFormL2UploadDocBank();
              }
              setFieldValue('bankAccountDocument', value);
            };

            const onFileDocumentHandler = ({ file: value }: { file: File | null; base64: string | null }): void => {
              if (value !== null) {
                segmentKycFormL2UploadDocOwner();
              }
              setFieldValue('identityDocument', value);
            };

            const onFileAddressHandler = ({ file: value }: { file: File | null; base64: string | null }): void => {
              if (value !== null) {
                segmentKycFormL2UploadDocAddress();
              }
              setFieldValue('proofOfAddressDocument', value);
            };

            const onFileTaxHandler = ({ file: value }: { file: File | null; base64: string | null }): void => {
              if (value !== null) {
                segmentKycFormL2UploadDocCompanyTax();
              }
              setFieldValue('taxCompliantDocument', value);
            };

            const onFileShareHoldersHandler = ({ file: value }: { file: File | null; base64: string | null }): void => {
              if (value !== null) {
                segmentKycFormL2UploadDocCompanyIncorporation();
              }
              setFieldValue('shareholdersDocument', value);
            };

            return (
              <form onSubmit={handleSubmit}>
                <FormObserver
                  displayed={displayed}
                  isCompany={isCompany}
                  missingDocuments={missingDocuments}
                  onChangeOnboardingMenu={onChangeMenu}
                />
                <Card>
                  <CardTitle>{l('companyInformation.kycTwo.headline')}</CardTitle>
                  <CardDescription>{l('companyInformation.kycTwo.subheadline')}</CardDescription>
                  <div className="kyc-level-2_note">{l('companyInformation.kyc.companyForm.allDataRequired')}</div>
                  {docsRequired.bankAccountDocument && (
                    <>
                      <div className="kyc-level-2_title">
                        <img src={FileIcon} alt="user" />
                        <span>{l('companyInformation.kycTwo.bankAccountDocumentation')}</span>
                      </div>
                      <UploadFile
                        title={
                          langKeyExists(`companyInformation.kycFour.bankAccountDocument.${country}`)
                            ? l(`companyInformation.kycFour.bankAccountDocument.${country}`)
                            : l('companyInformation.kycFour.bankAccountDocument')
                        }
                        description={l('companyInformation.kycFour.bankAccountDocumentHelp')}
                        supportedExtensions={config.uloadFileSupportedExtensions}
                        maxSize={config.uloadFileMaxSize}
                        onChange={onFileBankHandler}
                        externalError={(touched.bankAccountDocument && errors.bankAccountDocument) || undefined}
                      />
                    </>
                  )}
                  {(docsRequired.identityDocument || docsRequired.proofOfAddressDocument) && (
                    <div className="kyc-level-2_title">
                      <img src={UserIcon} alt="user" />
                      <span>{l('companyInformation.kycTwo.companyOwnerDocuments')}</span>
                    </div>
                  )}
                  {docsRequired.identityDocument && (
                    <>
                      <UploadFile
                        title={
                          isCompany
                            ? l('companyInformation.kycTwo.identityDocument')
                            : l('companyInformation.kycTwo.identityDocumentPhysical')
                        }
                        description={l('companyInformation.kycTwo.identityDocumentHelp')}
                        supportedExtensions={config.uloadFileSupportedExtensions}
                        maxSize={config.uloadFileMaxSize}
                        onChange={onFileDocumentHandler}
                        externalError={
                          (errors.identityDocument && touched.identityDocument && errors.identityDocument) || undefined
                        }
                      />
                      <br />
                      <br />
                    </>
                  )}
                  {docsRequired.proofOfAddressDocument && (
                    <UploadFile
                      title={
                        isCompany
                          ? l('companyInformation.kycTwo.proofOfAddressDocument')
                          : l('companyInformation.kycTwo.proofOfAddressDocumentPhysical')
                      }
                      description={
                        isCompany
                          ? l('companyInformation.kycTwo.proofOfAddressDocumentHelp')
                          : l('companyInformation.kycTwo.proofOfAddressDocumentPhysicalHelp')
                      }
                      supportedExtensions={config.uloadFileSupportedExtensions}
                      maxSize={config.uloadFileMaxSize}
                      onChange={onFileAddressHandler}
                      externalError={(touched.proofOfAddressDocument && errors.proofOfAddressDocument) || undefined}
                    />
                  )}
                  {(docsRequired.taxCompliantDocument || (docsRequired.shareholdersDocument && isCompany)) && (
                    <div className="kyc-level-2_title">
                      <img src={BuildingIcon} alt="user" />
                      <span>{l('companyInformation.kycTwo.companyDocuments')}</span>
                    </div>
                  )}
                  {docsRequired.taxCompliantDocument && (
                    <UploadFile
                      title={
                        langKeyExists(`companyInformation.kycTwo.taxCompliantDocument.${country}`)
                          ? l(`companyInformation.kycTwo.taxCompliantDocument.${country}`)
                          : l('companyInformation.kycTwo.taxCompliantDocument')
                      }
                      description={
                        isCompany
                          ? langKeyExists(`companyInformation.kycTwo.taxCompliantDocumentHelp.${country}`)
                            ? l(`companyInformation.kycTwo.taxCompliantDocumentHelp.${country}`)
                            : null
                          : null
                      }
                      supportedExtensions={config.uloadFileSupportedExtensions}
                      maxSize={config.uloadFileMaxSize}
                      onChange={onFileTaxHandler}
                      externalError={(touched.taxCompliantDocument && errors.taxCompliantDocument) || undefined}
                    />
                  )}
                  {docsRequired.shareholdersDocument && isCompany && (
                    <>
                      <br />
                      <br />
                      <UploadFile
                        title={
                          langKeyExists(`companyInformation.kycTwo.shareholdersDocument.${country}`)
                            ? l(`companyInformation.kycTwo.shareholdersDocument.${country}`)
                            : l('companyInformation.kycTwo.shareholdersDocument')
                        }
                        description={
                          langKeyExists(`companyInformation.kycTwo.shareholdersDocumentHelp.${country}`)
                            ? l(`companyInformation.kycTwo.shareholdersDocumentHelp.${country}`)
                            : l('companyInformation.kycTwo.shareholdersDocumentHelp')
                        }
                        supportedExtensions={config.uloadFileSupportedExtensions}
                        maxSize={config.uloadFileMaxSize}
                        onChange={onFileShareHoldersHandler}
                        externalError={(touched.shareholdersDocument && errors.shareholdersDocument) || undefined}
                      />
                    </>
                  )}
                  <div ref={errorRef}>
                    {error && (
                      <div className="kyc-level-2_message_error">
                        <MessageError
                          errorInfo={
                            errorInfo ?? {
                              level: 'error',
                              title: l('companyInformation.kycTwo.error.upload.title'),
                              message: l('companyInformation.kycTwo.error.upload.message'),
                            }
                          }
                        />
                      </div>
                    )}
                  </div>
                  <div className="kyc-level-2_send_btn">
                    {/* @ts-ignore */}
                    <ButtonCustom type="submit" fullWidth text={l('companyInformation.send')} />
                  </div>
                </Card>
              </form>
            );
          }}
        </Formik>
      </div>
    </div>
  );
};

export default SectionDocs;
